import { useRef, useState, useEffect } from "react";
import './Camera.css';
import TauriImage from "../../../../TauriImage/TauriImage";
import OpalAlertModal from "../../../../OpalAlertModal/OpalAlertModal";

const Camera = ({ onCapture, textOverlay, overlayPosition = "bottom" }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [imageData, setImageData] = useState(null);
    const [stream, setStream] = useState(null);
    const [cameraFacing, setCameraFacing] = useState("user");
    const [showConfirm, setShowConfirm] = useState(false);

    const checkCameraPermission = async () => {
        return new Promise((resolve) => {
            navigator.mediaDevices.getUserMedia({ video: true })
                .then(() => resolve(true))   // Permission granted
                .catch(() => resolve(false)); // Permission denied or other error
        });
    };

    const startCamera = async (mode = "user") => {
        try {
            // Stop old stream
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }

            const constraints = {
                video: { facingMode: { ideal: mode } },
                audio: false
            };

            const newStream = await navigator.mediaDevices.getUserMedia(constraints);
            if (videoRef.current) {
                videoRef.current.srcObject = newStream;
            }

            setStream(newStream);
        } catch (err) {
            console.error("Camera error:", err);
        }
    };

    const initializeCamera = async () => {
        // const permission = await checkCameraPermission();

        // if (!permission) {
        //     setShowConfirm(true);
        // }

        startCamera(cameraFacing);
    };

    useEffect(() => {
        startCamera(cameraFacing);
        return () => {
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
        };
    }, [cameraFacing]);

    const drawText = (ctx, text, canvasWidth, canvasHeight, padding, lineHeight, bottomMargin, overlayPosition) => {
        const words = text.split(" ");
        let line = "";
        let lines = [];

        // split text into lines that fit within canvas width minus padding
        const maxWidth = canvasWidth - padding * 2;
        for (let n = 0; n < words.length; n++) {
            const testLine = line + words[n] + " ";
            const testWidth = ctx.measureText(testLine).width;
            if (testWidth > maxWidth && n > 0) {
                lines.push(line.trim());
                line = words[n] + " ";
            } else {
                line = testLine;
            }
        }
        lines.push(line.trim());

        const blockHeight = lines.length * lineHeight;

        let startY;

        if (overlayPosition === "top") {
            startY = padding + lineHeight;
        }
        else if (overlayPosition === "center") {
            startY = (canvasHeight - blockHeight) / 2 + lineHeight;
        }
        else {
            // bottom (default)
            startY = canvasHeight - bottomMargin - blockHeight + lineHeight;
        }

        for (let i = 0; i < lines.length; i++) {
            const lineText = lines[i];
            const textWidth = ctx.measureText(lineText).width;
            const x = (canvasWidth - textWidth) / 2;
            const y = startY + i * lineHeight;

            ctx.strokeText(lineText, x, y);
            ctx.fillText(lineText, x, y);
        }
    };



    const captureImage = async () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (video && canvas) {
            const context = canvas.getContext("2d");
            const width = video.videoWidth;   // use real video width
            const height = video.videoHeight; // use real video height

            const scale = window.devicePixelRatio || 1;
            canvas.width = width * scale;
            canvas.height = height * scale;
            context.scale(scale, scale);

            context.clearRect(0, 0, canvas.width, canvas.height);

            context.drawImage(video, 0, 0, width, height);

            if (textOverlay) {
                const padding = 20;
                const bottomMargin = 40;

                let fontSize = Math.max(5, Math.floor(width / 35));
                if (width <= 480) fontSize = Math.floor(width / 30);
                if (width <= 360) fontSize = Math.floor(width / 35);
                context.font = `${fontSize}px Arial`;
                context.fillStyle = "white";
                context.strokeStyle = "black";
                context.lineWidth = 3;

                const lineHeight = fontSize + 5;
                drawText(context, textOverlay, width, height, padding, lineHeight, bottomMargin, overlayPosition);
            }


            const dataUrl = canvas.toDataURL("image/jpeg", 0.7);

            // Convert dataUrl to Blob
            const toBlob = await (await fetch(dataUrl)).blob();

            setImageData(dataUrl);
            onCapture?.(toBlob);
        }
    };

    const getCameraInstructions = () => {
        const userAgent = navigator.userAgent;

        if (/Chrome/i.test(userAgent) && !/Edg/i.test(userAgent)) {
            return `<p>Go to <strong>chrome://settings/content/camera</strong></p>`;
        } else if (/Edg/i.test(userAgent)) {
            return `<p>Go to <strong>edge://settings/content/camera</strong></p>`;
        } else if (/Firefox/i.test(userAgent)) {
            return `<p>Go to <strong>Settings > Privacy & Security > Permissions > Camera</strong></p>`;
        } else if (/Safari/i.test(userAgent) && /iPhone|iPad|iPod/i.test(userAgent)) {
            return `<p>Go to <strong>Settings > Safari > Camera</strong></p>`;
        } else if (
            /Safari/i.test(userAgent) &&
            /Macintosh/i.test(userAgent) &&
            !/Chrome|CriOS|Edg/i.test(userAgent)
        ) {
            return `<p>Go to <strong>Safari > Settings > Websites > Camera</strong></p>`;
        } else {
            return `<p>Your browser is blocking camera access. Please check your browser settings to allow camera permission.</p>`;
        }
    };

    return (
        <div className="coverage-camera">
            <div className="video-section">
                {imageData ? (
                    <TauriImage
                        className="captured-image"
                        localImage={imageData}
                        alt="Captured"
                    />
                ) : (
                    <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        className="camera-video"
                    />
                )}
            </div>

            <canvas ref={canvasRef} style={{ display: "none" }} />

            <div className="camera-controls">
                <TauriImage
                    className="camera-switch"
                    localImage="/Images/material-symbols--cameraswitch.svg"
                    onClick={() => setCameraFacing(prev => (prev === "environment" ? "user" : "environment"))}
                    color="#389EE7"
                    alt="Switch Camera"
                />
                <TauriImage
                    className="camera-shot"
                    localImage="/Images/camera.svg"
                    onClick={captureImage}
                    color="#389EE7"
                    alt="Capture"
                />
                <TauriImage
                    className="camera-back"
                    localImage="/Images/camera-back.svg"
                    onClick={() => {
                        setImageData(null);
                        initializeCamera(); // restart camera stream when resetting
                    }}
                    color="#389EE7"
                    alt="Clear"
                />
            </div>

            <OpalAlertModal
                show={showConfirm}
                onHide={() => setShowConfirm(false)}
                title="Allow the Camera"
                bodyText={getCameraInstructions()}
                modalType="message"
            />
        </div>
    );
};

export default Camera;
